// src/features/riskmize/RiskmizeMapOverlay.jsx
//
// Riskmize overlay bundle for any MapView host. Extracted from LiveMapPage
// (Phase 5) so both the Live Map and the Dispatch Board can render the same
// incidents layer, popup, and drawer. Keyed on a shared `mapViewRef`.
//
// Responsibilities:
//   1. Poll /api/riskmize/incidents via useRiskIncidents (bbox-aware).
//   2. Track map bounds into bboxRef on every 'moveend'.
//   3. Push layer visibility + incident data into MapView's imperative API
//      whenever the Redux slice changes.
//   4. Listen for 'riskmize:incident-click' dispatched by MapView, fetch the
//      full incident, and render IncidentPopup anchored to the clicked pin.
//   5. Mount IncidentsDrawer opposite the assets panel.
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import IncidentPopup from './IncidentPopup';
import IncidentsDrawer from './IncidentsDrawer';
import { riskmizeApi } from './riskmize.api';
import { useRiskIncidents } from './useRiskIncidents';

/**
 * @param {Object} props
 * @param {React.MutableRefObject<any>} props.mapViewRef — ref to the parent
 *   MapView instance (exposes getMap(), setIncidentsVisible, setIncidentsData)
 * @param {'start'|'end'} [props.panelSide] — logical side of the host's assets
 *   panel so the drawer can mount on the opposite side. Defaults to 'start'.
 */
export default function RiskmizeMapOverlay({ mapViewRef, panelSide = 'start' }) {
  const riskLayerEnabled = useSelector((s) => s.riskmize.layerEnabled);
  const riskIncidents    = useSelector((s) => s.riskmize.incidents);

  // Poll /api/riskmize/incidents on a 60s timer when the layer is enabled.
  // bboxRef.current is populated on each 'moveend' so we don't re-trigger
  // the polling effect on every map pan.
  /** @type {React.MutableRefObject<[number,number,number,number]|undefined>} */
  const bboxRef = useRef(undefined);
  useRiskIncidents(bboxRef);

  // Track map bounds → bboxRef on moveend. Attached once the map instance is
  // available (MapView mounts synchronously; getMap() is live after that).
  useEffect(() => {
    const map = mapViewRef.current?.getMap?.();
    if (!map) return undefined;
    const updateBbox = () => {
      const b = map.getBounds();
      bboxRef.current = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    };
    // Prime once the map's first style has loaded.
    if (map.loaded()) updateBbox();
    else map.once('load', updateBbox);
    map.on('moveend', updateBbox);
    return () => { map.off('moveend', updateBbox); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync visibility + incident data to MapView whenever the slice changes.
  useEffect(() => {
    mapViewRef.current?.setIncidentsVisible?.(Boolean(riskLayerEnabled));
  }, [riskLayerEnabled, mapViewRef]);

  useEffect(() => {
    mapViewRef.current?.setIncidentsData?.({
      type: 'FeatureCollection',
      features: Array.isArray(riskIncidents) ? riskIncidents : [],
    });
  }, [riskIncidents, mapViewRef]);

  // Riskmize incident popup — anchored over the clicked pin.
  // MapView dispatches a 'riskmize:incident-click' CustomEvent on its container
  // with { id, anchor:{x,y} } in `detail`. We fetch full incident detail here
  // (list endpoint returns a trimmed FC) and render IncidentPopup via portal.
  const [incidentPopup, setIncidentPopup] = useState(null);
  useEffect(() => {
    const map = mapViewRef.current?.getMap?.();
    const container = map?.getContainer?.();
    if (!container) return undefined;
    const onIncidentClick = async (e) => {
      const { id, anchor } = e.detail || {};
      if (!id || !anchor) return;
      try {
        const incident = await riskmizeApi.getIncident(id);
        setIncidentPopup({ incident, anchor });
      } catch (err) {
        // Fall back to opening detail in drawer; swallow popup error silently.
        console.warn('[riskmize] failed to load incident detail', err);
      }
    };
    container.addEventListener('riskmize:incident-click', onIncidentClick);
    return () => container.removeEventListener('riskmize:incident-click', onIncidentClick);
    // The map container is stable for the life of the mounted MapView, but
    // depend on mapViewRef.current indirectly by re-running once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Incident popup — anchored to the clicked map pin */}
      {incidentPopup && (
        <IncidentPopup
          incident={incidentPopup.incident}
          anchor={incidentPopup.anchor}
          onClose={() => setIncidentPopup(null)}
        />
      )}

      {/* Drawer — opens opposite the vehicles/assets panel */}
      <IncidentsDrawer
        assetsPanelSide={panelSide}
        onZoomTo={(coords) => {
          const map = mapViewRef.current?.getMap?.();
          if (map && coords) map.flyTo({ center: coords, zoom: 14 });
        }}
      />
    </>
  );
}
